import React, { useState, useRef, useCallback } from 'react';
import { Sparkles, ArrowRight } from 'lucide-react';

const Slider = ({ pair }) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPosition(pct);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    updatePosition(e.clientX);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updatePosition(e.clientX);
  };

  const onPointerUp = () => {
    dragging.current = false;
  };

  const objectPos = pair.objectPosition || {};

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-sm md:text-lg font-bold text-gray-900">{pair.label}</h3>
        <span className="text-xs md:text-sm text-cyan-600 font-medium">{pair.description}</span>
      </div>
      <div
        ref={containerRef}
        className="relative w-full aspect-[16/9] rounded-xl overflow-hidden cursor-col-resize select-none touch-none shadow-lg"
        data-testid={`before-after-slider-${pair.id}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Before image */}
        <img
          src={pair.beforeImage || pair.image}
          alt="Avant"
          style={objectPos}
          className={`absolute inset-0 w-full h-full object-cover ${pair.beforeImage ? '' : 'brightness-[0.55] saturate-[0.3] sepia-[0.4] contrast-[0.9]'}`}
          draggable={false}
        />

        {/* After image (clipped) */}
        <div
          className="absolute inset-0"
          style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        >
          <img
            src={pair.afterImage || pair.image}
            alt="Après"
            style={objectPos}
            className="w-full h-full object-cover"
            draggable={false}
          />
        </div>

        {/* Labels */}
        <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          Avant
        </div>
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-900 text-[10px] md:text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
          Après
        </div>

        {/* Slider line */}
        <div
          className="absolute top-0 bottom-0 w-[3px] bg-white shadow-[0_0_8px_rgba(0,0,0,0.4)]"
          style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
        >
          {/* Handle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 bg-white rounded-full shadow-xl flex items-center justify-center">
            <div className="flex items-center gap-0.5 text-gray-600">
              <svg width="7" height="14" viewBox="0 0 7 14" fill="currentColor"><path d="M5.5 7L0.5 1v12l5-6z"/></svg>
              <svg width="7" height="14" viewBox="0 0 7 14" fill="currentColor"><path d="M1.5 7L6.5 1v12l-5-6z"/></svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaRow = ({ label, description, leftContent, rightContent }) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <h3 className="text-sm md:text-lg font-bold text-gray-900">{label}</h3>
        <span className="text-xs md:text-sm text-cyan-600 font-medium">{description}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl overflow-hidden shadow-lg aspect-[4/3]">
          {leftContent}
        </div>
        <div className="rounded-xl overflow-hidden shadow-lg aspect-[4/3]">
          {rightContent || null}
        </div>
      </div>
    </div>
  );
};

const Video = ({ src, poster }) => (
  <video autoPlay loop muted playsInline poster={poster} className="w-full h-full object-cover">
    <source src={src} type="video/mp4" />
  </video>
);

const Img = ({ src, alt }) => (
  <img src={src} alt={alt} className="w-full h-full object-cover" />
);

const BeforeAfterGallery = () => {
  const sliderPairs = [
    {
      id: 3,
      label: 'Lavage de gouttière',
      description: 'Résultat impeccable',
      beforeImage: '/before-lavage.png',
      afterImage: '/after-lavage.png',
      objectPosition: { objectPosition: '50% 30%' },
    },
  ];

  return (
    <section id="gallery" className="py-8 md:py-12 bg-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-cyan-50 text-cyan-700 rounded-full px-4 py-2 mb-4">
            <Sparkles size={18} />
            <span className="text-sm font-semibold">Nos services</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            Nos{' '}
            <span className="bg-gradient-to-r from-cyan-600 via-blue-600 to-pink-500 bg-clip-text text-transparent">
              réalisations
            </span>
          </h2>
          <p className="text-sm md:text-base text-gray-500">
            Glissez pour voir la différence — Avant et après nos interventions
          </p>
        </div>

        <div className="max-w-4xl mx-auto space-y-6 md:space-y-8">
          {/* Row 1 - Lavage de fenêtres: photo maison jaune + vidéo boomerang */}
          <MediaRow
            label="Lavage de fenêtres"
            description="Osmose écologique"
            leftContent={<Img src="/maison-jaune.jpeg" alt="Lavage de fenêtres" />}
            rightContent={<Video src="/lavage-fenetre.mp4" poster="/poster-lavage.jpg" />}
          />

          {/* Row 2 - Soft wash: vidéo gauche + vidéo droite */}
          <MediaRow
            label="Soft wash résidentiel"
            description="Nettoyage doux et efficace"
            leftContent={<Video src="/softwash.mp4" poster="/poster-softwash.jpg" />}
            rightContent={<Video src="/softwash-right.mp4" poster="/poster-softwash-right.jpg" />}
          />

          {/* Slider 3 - Lavage de gouttière (avant/après) */}
          <Slider pair={sliderPairs[0]} />
        </div>

        {/* CTA */}
        <div className="text-center mt-8 md:mt-12">
          <button
            data-testid="gallery-cta"
            onClick={() => {
              const el = document.getElementById('contact');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-4 rounded-full font-bold text-base md:text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            Demander une estimation gratuite
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterGallery;
